import { FavoriteApi } from './FavoriteApi';
import { Log } from '@lightningjs/sdk';
import { BaseCachedApi } from '../BaseCachedApi';
import { Backend } from '../Backend';
import { IFavoriteElementV1 } from '../models/AndromeTV.types';
import { FavoriteID } from './FavoriteApi.types';
import { SettingsKeys } from '../models/TranslationKeys';

export class CachedFavoriteApi extends BaseCachedApi {
  protected override _cache: Map<string, IFavoriteElementV1> | undefined;

  constructor(private backend: Backend) {
    super();
    this.updateGlobalSettings();
  }

  private updateGlobalSettings() {
    this.updateExpirations(
      this.backend.globalSettings.numberValue(SettingsKeys.favoriteCacheExpiration) ?? 600000,
      this.backend.globalSettings.numberValue(SettingsKeys.favoriteCacheErrorExpiration) ?? 300000
    );
  }

  list(backend: Backend, showAdult: boolean): Promise<IFavoriteElementV1[]> {
    const prom = this.checkCache(backend);
    return prom.then(() => {
      if (this._cache) return Array.from(this._cache.values()).filter(fav => showAdult || !fav.adult);
      return [];
    });
  }

  setFavorite(backend: Backend, favoriteId: FavoriteID, adult: boolean, schedules = []): Promise<void> {
    if (!backend.isAuthorized()) return Promise.resolve();

    const prom = this.checkCache(backend);

    return prom.then(() => {
      this._cache?.set(this.favoriteKey(favoriteId), {
        type: favoriteId.type,
        id: favoriteId.id,
        adult,
        schedules,
        lastUpdate: (new Date().getTime() / 1000).toString(),
      });
      FavoriteApi.update(backend, favoriteId);
    });
  }

  deleteFavorite(backend: Backend, favoriteId: FavoriteID) {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      this._cache?.delete(this.favoriteKey(favoriteId));
      FavoriteApi.delete(backend, favoriteId);
    });
  }

  getFavorite(backend: Backend, favoriteId: FavoriteID): Promise<IFavoriteElementV1 | undefined> {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      return this._cache?.get(this.favoriteKey(favoriteId));
    });
  }

  isFavorite(backend: Backend, favoriteId: FavoriteID): Promise<boolean> {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      return this._cache?.has(this.favoriteKey(favoriteId)) || false;
    });
  }

  isFavoriteMap(backend: Backend, list: FavoriteID[]): Promise<Map<string, boolean>> {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      const map = new Map();
      list.forEach(favoriteId => {
        map.set(favoriteId.id, this._cache?.has(this.favoriteKey(favoriteId)) || false);
      });
      return map;
    });
  }

  override fillCache(backend: Backend) {
    this.updateGlobalSettings();
    if (!backend.isAuthorized()) {
      this.clearCache();
      return Promise.resolve(new Map());
    }

    Log.info('[CachedFavoriteApi]', 'refreshing list, bypass:', this._bypassCache);
    return FavoriteApi.list(backend, {
      profileId: backend.deviceProfile?.id,
      catalogId: backend.deviceProfile?.profile.catalog,
    }).then(result => {
      this.clearCache();
      if (!this._cache) this._cache = new Map();
      result.forEach(element => {
        this._cache?.set(this.favoriteKey(element), element);
      });
      return this._cache;
    });
  }

  favoriteKey(fav: IFavoriteElementV1 | FavoriteID) {
    return fav.id + fav.type;
  }
}
